/**
 * The wire rules behind the TAX_AMOUNT_FORM card's boxes — which boxes are on screen,
 * what starts in them, and exactly what a submission says.
 *
 * Kept OUT of `TaxSummaryCardV2.vue` for the same reason `taxSimulationCard.js` is: a
 * template cannot be unit-tested, and every function here decides something the backend
 * will file as a customer's answer. Pinned by `tests/taxShortFormCard.test.mjs`.
 *
 * 🔴 **Nothing here is a tax rule and nothing here judges an answer.** The backend is the
 * single decider of what a submitted string means (`TaxShortFormCardV2.NormalizeAmount` →
 * `ReadPostback`). What this file does is keep the message we send INSIDE the wire
 * contract (`TaxPlanningV2-CardPostback-Wire-Design.md` §1.4): keys echoed from the card,
 * values as plain-digit strings, every box on screen present every time.
 *
 * `local` throughout is `{ [fieldId]: string }` — the TEXT the customer has typed into
 * each box since the current card arrived. A box absent from it has not been touched.
 */

/**
 * The boxes the card really draws, flattened out of `groups[].fields[]`.
 *
 * 🔴 A field with no `id` is DROPPED. `id` IS the FieldKey a submission writes to, so a box
 * without one could be typed in and then be missing from `values` — a screen that shows
 * more than it can send, which is the one thing the three-state rule below cannot survive.
 */
export function flattenFields(groups) {
  const list = Array.isArray(groups) ? groups : [];
  return list.reduce((all, g) => {
    const fields = g && Array.isArray(g.fields) ? g.fields : [];
    return all.concat(fields.filter(f => f && f.id != null && f.id !== ''));
  }, []);
}

/**
 * One entry per ANSWER this card can send, which is not the same list as the one it DRAWS.
 *
 * 🔴 A `checkbox` row carrying `options` is a HEADING, not a box: each option answers its
 * own `id` and the heading answers nothing (`TaxShortFormFieldV2Dto.options` — the backend
 * drops the heading from `MemberFieldKeys` for the same reason). Every function below that
 * decides what a submission SAYS goes through here, so a tick under a heading is seeded,
 * validated and sent exactly like the standalone tick it used to be — while `flattenFields`
 * keeps returning the rows, because that is what the screen draws.
 *
 * 🔴 An option's starting state comes from `checked`, not from `value`: the heading has no
 * `value` and reusing it would give all of its ticks one shared state.
 */
function answerBoxes(fields) {
  return (fields || []).reduce((all, f) => {
    const options = f && Array.isArray(f.options) ? f.options.filter(o => o && o.id != null && o.id !== '') : [];
    if (!options.length) return all.concat(f ? [f] : []);
    return all.concat(options.map(o => ({
      id: o.id,
      // Ticks travel as the plain-digit strings every other box on this card uses, so
      // nothing downstream needs to know a checkbox was involved.
      value: o.checked ? '1' : '0',
      control: 'CHECKBOX',
      type: 'checkbox',
    })));
  }, []);
}

/** The same drop rule, kept grouped for drawing — a group left with no box goes with it. */
export function renderableGroups(groups) {
  const list = Array.isArray(groups) ? groups : [];
  return list
    .map(g => ({ ...g, fields: flattenFields([g]) }))
    .filter(g => g.fields.length > 0);
}

/**
 * The starting text of a freshly arrived card, by box id.
 *
 * 🔴 Wire contract §1.5: a field WITH a `value` prefills to it (that is an amount the
 * customer already stated), a field without one starts empty — `placeholder` is a hint and
 * never a value. Verbatim, so the box shows exactly what the card carried.
 */
export function seedValues(fields) {
  const next = {};
  answerBoxes(fields).forEach((f) => {
    if (f && f.id != null) next[f.id] = f.value == null ? '' : String(f.value);
  });
  return next;
}

/** What is IN one box right now: the customer's typing, else empty. */
export function fieldValue(field, local) {
  if (!field || field.id == null) return '';
  const held = local ? local[field.id] : undefined;
  return held == null ? '' : String(held);
}

/**
 * One box's text as the wire wants it: plain digits, no commas, no unit, no spaces.
 *
 * Returns `''` for a box left empty — that is a valid answer ("I have none"), not a bad
 * one — and `null` for text that holds no readable amount, which is the ONE case a caller
 * must not send silently (see `invalidFieldIds`).
 *
 * 🔴 The unit ("บาท") is printed INSIDE the box on this card, so typing it back is normal
 * behaviour rather than a mistake — `"50,000 บาท"` is what a careful customer produces.
 * Left verbatim it reaches the backend unparseable, gets counted as an ignored key, and the
 * node then treats that row as one that was never on screen: the amount disappears with
 * nothing on screen to say so. Stripping the decoration here is following the contract, not
 * overruling the backend — it still parses, still applies the ceilings, still has the last
 * word on every value.
 *
 * 🔴 Negative is `null`, not a number: the backend refuses it too (a minus in a submitted
 * amount is far more likely to be a stray hyphen than a claim), so accepting it here would
 * only move the silent drop one step later.
 */
export function normalizeAmount(raw) {
  const text = raw == null ? '' : String(raw);
  if (!text.trim()) return '';
  // Everything that is not a digit, a decimal point or a sign is decoration: thousands
  // separators, spaces, and the unit the box itself invites.
  const cleaned = text.replace(/[^0-9.-]/g, '');
  if (!cleaned || !/[0-9]/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  // Same shape the backend stores: whole baht plain, satang kept. A figure big enough to
  // print as "1e+21" is not an amount anyone typed on purpose, and sending it in that form
  // would be a value the backend reads as something else again — flag it instead.
  const out = String(n);
  return out.includes('e') ? null : out;
}

/**
 * The boxes whose text holds no amount we could send. Empty boxes are NOT in here.
 *
 * 🔴 This is what stops a typo from being filed as a zero. Without it the value is dropped
 * by the backend, the row is read as "never on screen", and the checkpoint still closes as
 * answered — a number the customer watched themselves type, gone, with no signal anywhere.
 */
export function invalidFieldIds(fields, local) {
  const bad = [];
  answerBoxes(fields).forEach((f) => {
    if (!f || f.id == null) return;
    if (normalizeAmount(fieldValue(f, local)) === null) bad.push(f.id);
  });
  return bad;
}

/**
 * The payload of a submission: EVERY box on screen, as text — three states, all three
 * meaning something different to the backend:
 *   digits  → that amount
 *   `''`    → a box the customer LOOKED AT and left empty ⇒ "I have none" ⇒ stored 0
 *   absent  → a box that was never on their screen (a gated-off row) ⇒ left untouched
 *
 * 🔴 Every box, every time — not just the ones that were typed in. Sending only the filled
 * boxes collapses the first two rows of that table into the third, and a row the customer
 * deliberately cleared keeps whatever amount the form already held. That is the 2026-08-09
 * bug this card exists to close; it looks exactly like a harmless optimisation.
 *
 * 🔴 `decline` sends the same key set with every value blank rather than sending nothing:
 * the button says "none of these apply to me", so the record shows how many boxes were on
 * screen when it was pressed.
 *
 * 🔴 Text that normalises to nothing is sent VERBATIM rather than as `''`. Callers gate
 * submit on `invalidFieldIds`, so it should not get here — but if it does, the backend
 * dropping an unreadable string is far better than us quietly filing a typo as a declared
 * zero. Strings throughout: the whole tax stack stores amounts as text on both ends.
 */
export function submissionValues(fields, local, action) {
  const blank = action === 'decline';
  const values = {};
  answerBoxes(fields).forEach((f) => {
    if (!f || f.id == null) return;
    if (blank) {
      values[f.id] = '';
      return;
    }
    const typed = fieldValue(f, local);
    const normalized = normalizeAmount(typed);
    values[f.id] = normalized === null ? typed : normalized;
  });
  return values;
}
